import { z } from 'zod';

import {
  COMPENSATION_TYPES,
  COVERAGE_STATUSES,
  PRAYER_SLOTS,
  type CompensationType,
  type CoverageStatus,
  type PrayerSlot,
} from '@/shared/model/enums';

export interface CoverageRequest {
  readonly id: string;
  readonly masjidId: string;
  readonly createdBy: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly prayerSlots: readonly PrayerSlot[];
  readonly notes: string | null;
  readonly status: CoverageStatus;
  readonly compensation: CompensationType;
  readonly acceptedApplicationId: string | null;
  readonly createdAt: string;
  readonly masjidName: string | null;
  readonly masjidCity: string | null;
  readonly masjidLat: number | null;
  readonly masjidLng: number | null;
}

export const isSingleDay = (request: CoverageRequest): boolean =>
  request.startDate === request.endDate;

const masjidEmbed = z
  .object({
    name: z.string(),
    city: z.string().nullable(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
  })
  .nullable();

const requestRowSchema = z.object({
  id: z.string(),
  masjid_id: z.string(),
  created_by: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  prayer_slots: z.array(z.enum(PRAYER_SLOTS)),
  notes: z.string().nullable(),
  status: z.enum(COVERAGE_STATUSES),
  compensation: z.enum(COMPENSATION_TYPES),
  accepted_application_id: z.string().nullable(),
  created_at: z.string(),
  masjids: masjidEmbed.optional(),
});

export function mapCoverageRequest(row: unknown): CoverageRequest {
  const r = requestRowSchema.parse(row);
  return {
    id: r.id,
    masjidId: r.masjid_id,
    createdBy: r.created_by,
    startDate: r.start_date,
    endDate: r.end_date,
    prayerSlots: r.prayer_slots,
    notes: r.notes,
    status: r.status,
    compensation: r.compensation,
    acceptedApplicationId: r.accepted_application_id,
    createdAt: r.created_at,
    masjidName: r.masjids?.name ?? null,
    masjidCity: r.masjids?.city ?? null,
    masjidLat: r.masjids?.latitude ?? null,
    masjidLng: r.masjids?.longitude ?? null,
  };
}

export interface CoverageRequestCreateInput {
  readonly masjidId: string;
  readonly createdBy: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly prayerSlots: readonly PrayerSlot[];
  readonly notes: string | null;
  readonly compensation: CompensationType;
}

export function toRequestRow(input: CoverageRequestCreateInput): Record<string, unknown> {
  return {
    masjid_id: input.masjidId,
    created_by: input.createdBy,
    start_date: input.startDate,
    end_date: input.endDate,
    prayer_slots: input.prayerSlots,
    notes: input.notes,
    compensation: input.compensation,
  };
}
